import React, { useState, useEffect, useMemo } from "react";
import { http } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { 
  Search, 
  Filter, 
  Eye, 
  RefreshCw, 
  FileText, 
  Calendar, 
  User, 
  Settings,
  ChevronDown,
  ChevronUp,
  Download
} from "lucide-react";
import OrdenDetalleModal from "./OrdenDetalleModal";

export default function MisOrdenes() {
  const { user } = useAuth();
  const [ordenes, setOrdenes] = useState([]);
  const [ordenesFiltradas, setOrdenesFiltradas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ordenDetalle, setOrdenDetalle] = useState(null);
  
  // Estados para filtros y búsqueda
  const [busqueda, setBusqueda] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [filtroFecha, setFiltroFecha] = useState('todos');
  const [filtroTipoServicio, setFiltroTipoServicio] = useState('todos');
  const [filtroTecnico, setFiltroTecnico] = useState('todos');
  const [mostrarFiltros, setMostrarFiltros] = useState(false);

  useEffect(() => {
    cargarMisOrdenes();
  }, []);

  // Filtrar órdenes cuando cambian los filtros o la búsqueda
  useEffect(() => {
    filtrarOrdenes();
  }, [ordenes, busqueda, filtroEstado, filtroFecha, filtroTipoServicio, filtroTecnico]);

  const filtrarOrdenes = () => {
    let resultado = [...ordenes];

    // Filtro por búsqueda
    if (busqueda.trim()) {
      resultado = resultado.filter(orden => 
        orden.numero_orden?.toLowerCase().includes(busqueda.toLowerCase()) ||
        orden.equipo_descripcion?.toLowerCase().includes(busqueda.toLowerCase()) ||
        orden.nombre_tecnico?.toLowerCase().includes(busqueda.toLowerCase())
      );
    }

    // Filtro por estado
    if (filtroEstado !== 'todos') {
      resultado = resultado.filter(orden => orden.estado === filtroEstado);
    }

    // Filtro por tipo de servicio
    if (filtroTipoServicio !== 'todos') {
      resultado = resultado.filter(orden => orden.tipo_servicio === filtroTipoServicio);
    }

    // Filtro por técnico
    if (filtroTecnico !== 'todos') {
      resultado = resultado.filter(orden => {
        const nombre = orden.nombre_tecnico || orden.tecnico_nombre || "Sin asignar";
        return nombre === filtroTecnico;
      });
    }

    // Filtro por fecha
    if (filtroFecha !== 'todos') {
      const ahora = new Date();
      resultado = resultado.filter(orden => {
        if (!orden.fecha) return false;
        const fechaOrden = new Date(orden.fecha);
        
        switch (filtroFecha) {
          case 'hoy':
            return fechaOrden.toDateString() === ahora.toDateString();
          case 'semana':
            const inicioSemana = new Date(ahora);
            inicioSemana.setDate(ahora.getDate() - 7);
            return fechaOrden >= inicioSemana;
          case 'mes':
            const inicioMes = new Date(ahora);
            inicioMes.setDate(ahora.getDate() - 30);
            return fechaOrden >= inicioMes;
          default:
            return true;
        }
      });
    }

    setOrdenesFiltradas(resultado);
  };

  async function cargarMisOrdenes() {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Cargando mis órdenes...');
      const res = await http.get('/cliente/mis-ordenes');
      
      if (Array.isArray(res.data)) {
        console.log('Órdenes recibidas:', res.data);
        setOrdenes(res.data);
      } else {
        console.warn('La respuesta no es un array:', res.data);
        setOrdenes([]);
      }
    } catch (error) {
      console.error('Error cargando mis órdenes:', error);
      setError('Error al cargar las órdenes. Por favor intente nuevamente.');
    } finally {
      setLoading(false);
    }
  }

  const formatearFecha = (fecha) => {
    if (!fecha) return "Sin fecha";
    try {
      return new Date(fecha).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch (e) {
      return "Fecha inválida";
    }
  };

  const getEstadoBadge = (estado) => {
    const estados = {
      pendiente: { color: "bg-yellow-100 text-yellow-800 border-yellow-200", texto: "Pendiente" },
      en_proceso: { color: "bg-emerald-100 text-emerald-800 border-emerald-200", texto: "En Proceso" }, 
      completada: { color: "bg-green-100 text-green-800 border-green-200", texto: "Completada" },
      borrador: { color: "bg-gray-100 text-gray-800 border-gray-200", texto: "Borrador" },
      cancelada: { color: "bg-red-100 text-red-800 border-red-200", texto: "Cancelada" },
    };
    
    const estadoInfo = estados[estado] || estados.pendiente;
    
    return (
      <span className={`px-3 py-1 text-xs font-medium rounded-full border ${estadoInfo.color}`}>
        {estadoInfo.texto}
      </span>
    );
  };

  const getEstadisticas = () => {
    const total = ordenes.length;
    const pendientes = ordenes.filter(o => o.estado === 'pendiente').length;
    const enProceso = ordenes.filter(o => o.estado === 'en_proceso').length;
    const completadas = ordenes.filter(o => o.estado === 'completada').length;
    
    return { total, pendientes, enProceso, completadas };
  };

  // Helper para obtener valores únicos para los filtros
  const getUniqueValues = (key) => {
    const values = ordenes.map(o => {
      if (key === 'nombre_tecnico') {
        return o.nombre_tecnico || o.tecnico_nombre || "Sin asignar";
      }
      return o[key];
    }).filter(Boolean);
    return [...new Set(values)].sort();
  };

  // Agrupar órdenes filtradas por mes
  const ordenesPorMes = useMemo(() => {
    const grupos = {};
    ordenesFiltradas.forEach(orden => {
      const fecha = orden.fecha ? new Date(orden.fecha) : null;
      const key = fecha
        ? `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}`
        : 'sin-fecha';
      const label = fecha
        ? fecha.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })
        : 'Sin fecha';
      if (!grupos[key]) {
        grupos[key] = { key, label: label.charAt(0).toUpperCase() + label.slice(1), ordenes: [] };
      }
      grupos[key].ordenes.push(orden);
    });
    return Object.values(grupos).sort((a, b) => b.key.localeCompare(a.key));
  }, [ordenesFiltradas]);

  // Estado para meses colapsados
  const [mesesColapsados, setMesesColapsados] = useState({});
  const toggleMes = (key) => setMesesColapsados(prev => ({ ...prev, [key]: !prev[key] }));

  const getFirmante = (orden) => {
    return orden.aclaracion_firma_cliente || orden.aclaracion_firma || orden.firma_aclaracion || '';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 md:p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="flex flex-col items-center gap-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
              <span className="text-gray-600 font-medium">Cargando sus órdenes...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 md:p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-2xl mx-auto">
            <div className="flex items-center gap-3">
              <FileText className="w-6 h-6 text-red-600" />
              <div>
                <h3 className="text-lg font-semibold text-red-800">Error al cargar órdenes</h3>
                <p className="text-red-700 mt-1">{error}</p>
              </div>
            </div>
            <button 
              onClick={cargarMisOrdenes}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Reintentar
            </button>
          </div>
        </div>
      </div>
    );
  }

  const estadisticas = getEstadisticas();

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                Mis Órdenes de Servicio
              </h1>
              <p className="text-gray-600">
                Gestión y seguimiento de todas sus órdenes de servicio
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={cargarMisOrdenes}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
              >
                <RefreshCw size={16} />
                Actualizar
              </button>
            </div>
          </div>
        </div>

        {/* Estadísticas */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <FileText className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total</p>
                <p className="text-xl font-bold text-gray-900">{estadisticas.total}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Calendar className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Pendientes</p>
                <p className="text-xl font-bold text-gray-900">{estadisticas.pendientes}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Settings className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">En Proceso</p>
                <p className="text-xl font-bold text-gray-900">{estadisticas.enProceso}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <FileText className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Completadas</p>
                <p className="text-xl font-bold text-gray-900">{estadisticas.completadas}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Barra de búsqueda y filtros */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Búsqueda */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Buscar por número de orden, equipo o técnico..."
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
            </div>
            
            {/* Botón filtros */}
            <button
              onClick={() => setMostrarFiltros(!mostrarFiltros)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Filter size={16} />
              Filtros
              <ChevronDown size={16} className={`transform transition-transform ${mostrarFiltros ? 'rotate-180' : ''}`} />
            </button>
          </div>
          
          {/* Panel de filtros */}
          {mostrarFiltros && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Estado</label>
                  <select
                    value={filtroEstado}
                    onChange={(e) => setFiltroEstado(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  >
                    <option value="todos">Todos los estados</option>
                    <option value="borrador">Borrador</option>
                    <option value="completada">Completada</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tipo Servicio</label>
                  <select
                    value={filtroTipoServicio}
                    onChange={(e) => setFiltroTipoServicio(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  >
                    <option value="todos">Todos</option>
                    {getUniqueValues('tipo_servicio').map(tipo => (
                      <option key={tipo} value={tipo}>
                        {tipo.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Técnico</label>
                  <select
                    value={filtroTecnico}
                    onChange={(e) => setFiltroTecnico(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  >
                    <option value="todos">Todos</option>
                    {getUniqueValues('nombre_tecnico').map(tech => (
                      <option key={tech} value={tech}>{tech}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Fecha</label>
                  <select
                    value={filtroFecha}
                    onChange={(e) => setFiltroFecha(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  >
                    <option value="todos">Todas las fechas</option>
                    <option value="hoy">Hoy</option>
                    <option value="semana">Última semana</option>
                    <option value="mes">Último mes</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Lista de órdenes agrupadas por mes */}
        {ordenesFiltradas.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {ordenes.length === 0 ? 'No tiene órdenes de servicio' : 'No se encontraron órdenes'}
            </h3>
            <p className="text-gray-600">
              {ordenes.length === 0 
                ? 'Las órdenes aparecerán aquí cuando nuestros técnicos las registren.'
                : 'Intente ajustar los filtros de búsqueda.'
              }
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {ordenesPorMes.map((grupo) => {
              const colapsado = mesesColapsados[grupo.key];
              return (
                <div key={grupo.key} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  {/* Header del mes */}
                  <button
                    onClick={() => toggleMes(grupo.key)}
                    className="w-full px-6 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors border-b border-gray-200"
                  >
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-emerald-600" />
                      <h3 className="text-base font-semibold text-gray-900">{grupo.label}</h3>
                      <span className="text-xs bg-emerald-100 text-emerald-700 font-medium px-2 py-0.5 rounded-full">
                        {grupo.ordenes.length} {grupo.ordenes.length === 1 ? 'orden' : 'órdenes'}
                      </span>
                    </div>
                    {colapsado ? <ChevronDown size={18} className="text-gray-400" /> : <ChevronUp size={18} className="text-gray-400" />}
                  </button>

                  {/* Tabla del mes */}
                  {!colapsado && (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              N° Orden
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Fecha
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Técnico
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Tipo Servicio
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Firmante
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Estado
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Acciones
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {grupo.ordenes.map((orden) => (
                            <tr key={orden.id} className="hover:bg-gray-50 transition-colors">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">
                                  {orden.numero_orden}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">
                                  {formatearFecha(orden.fecha)}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <User className="w-4 h-4 text-gray-400 mr-2" />
                                  <span className="text-sm text-gray-900 capitalize">
                                    {orden.nombre_tecnico || orden.tecnico_nombre || "Sin asignar"}
                                  </span>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className="text-sm text-gray-600 capitalize bg-gray-100 px-2 py-1 rounded-md">
                                  {(orden.tipo_servicio || "mantenimiento_preventivo").replace(/_/g, " ")}
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                <div className="text-sm text-gray-900 max-w-[200px] truncate" title={getFirmante(orden)}>
                                  {getFirmante(orden) || <span className="text-gray-400 italic">Sin firma</span>}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                {getEstadoBadge(orden.estado)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                <button
                                  onClick={() => setOrdenDetalle(orden)}
                                  className="flex items-center gap-1 text-emerald-600 hover:text-emerald-900 transition-colors"
                                >
                                  <Eye size={16} />
                                  Ver detalle
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Modal de detalle */}
        {ordenDetalle && (
          <OrdenDetalleModal
            orden={ordenDetalle}
            onClose={() => setOrdenDetalle(null)}
          />
        )}
      </div>
    </div>
  );
}